/**
 * Bank-transfer invoice endpoints («Оплата по счёту»).
 *
 * A separate module rather than lines in `api.ts`, per that file's own rule:
 * domain clients call `apiRequest`, `api.ts` stays free of business endpoints.
 */
import { apiBaseUrl, apiRequest, checkoutAuthHeader, getToken } from './api';
import { needsServerDownload } from './in-app-browser';
import { saveBlob } from './save-blob';

export interface IssuedInvoice {
  id: string;
  number: string;
  amountUzs: number;
  planKey: string;
  planName: string;
  buyerName: string;
  buyerTin: string | null;
  /** The per-invoice payment purpose the buyer must copy into their transfer. */
  purpose: string;
  /** Relative to the API base — see `invoicePdfUrl`. */
  pdfUrl: string;
  issuedAt: string;
  emailSentAt: string | null;
  telegramSentAt: string | null;
}

export interface BuyerLookup {
  tin: string;
  /** `null` when the ИНН is not in our organisations table — NOT an error. */
  name: string | null;
}

export type InvoiceChannel = 'email' | 'telegram';

/** Company name for an ИНН, so the buyer confirms instead of typing. */
export async function lookupBuyer(tin: string): Promise<BuyerLookup> {
  return apiRequest<BuyerLookup>(`/billing/invoice/buyer?tin=${encodeURIComponent(tin)}`);
}

export async function createInvoice(input: {
  planKey: string;
  buyerTin?: string;
  buyerName?: string;
}): Promise<IssuedInvoice> {
  return apiRequest<IssuedInvoice>('/billing/invoice', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function listInvoices(): Promise<IssuedInvoice[]> {
  return apiRequest<IssuedInvoice[]>('/billing/invoices');
}

export async function sendInvoice(
  id: string,
  channel: InvoiceChannel,
): Promise<{ ok: boolean; channel: InvoiceChannel }> {
  return apiRequest(`/billing/invoices/${encodeURIComponent(id)}/send`, {
    method: 'POST',
    body: JSON.stringify({ channel }),
  });
}

/**
 * Downloads the PDF.
 *
 * Fetched as a blob rather than dropped into an `<a href>`: the session token
 * lives in localStorage, so a plain link would arrive unauthenticated for
 * anyone whose cookie is absent (Safari ITP, an embedded browser) and download
 * a JSON 401 named `.pdf`. Mirrors `downloadCatalogExport`.
 *
 * ⚠️ This is the ONE call in this module that does not go through `apiRequest`
 * (a blob cannot), so it applies the checkout-token rule by hand rather than
 * inheriting it. `pdfUrl` is a `/billing/...` path, so a guest who issued an
 * invoice and whose `commit()` did not land can still download the document
 * they just created instead of saving a 401 named `.pdf`.
 */
/**
 * A short-lived URL that downloads the PDF without an `Authorization` header.
 *
 * `/billing/…`, so a guest's checkout token rides along exactly as it does on
 * the blob fetch below.
 */
export async function createInvoiceDownloadTicket(
  id: string,
): Promise<{ url: string; expiresInSeconds: number }> {
  return apiRequest(`/billing/invoices/${encodeURIComponent(id)}/download-ticket`, {
    method: 'POST',
  });
}

export async function downloadInvoicePdf(invoice: IssuedInvoice): Promise<void> {
  /*
   * ANDROID WEBVIEWS AND iOS IN-APP BROWSERS TAKE THE OTHER ROAD.
   *
   * A WebView cannot save a `blob:` URL — downloads go through the host app's
   * `DownloadListener`, which is only offered `http(s)` URLs, and `<a download>`
   * is ignored. Nothing about that is detectable after the fact: the fetch
   * resolves, `saveBlob` runs to completion, and no file appears. Measured
   * 2026-09-21 for `samursamar708@…` in the Instagram WebView on Android 10 —
   * 18 requests to the PDF endpoint in 29 seconds (one 200 with 421 906 bytes,
   * then 17 × 304), three PostHog `$rageclick`s, zero files.
   *
   * So there we never make a blob at all. We ask the API for a ticketed URL and
   * navigate to it; it answers `Content-Disposition: attachment`, which does not
   * navigate the page anywhere — the download listener takes it and the sheet
   * stays exactly where it was.
   *
   * ⚠️ `location.assign`, not `window.open`: a WebView blocks a programmatic
   * new tab far more often than it blocks a navigation, and a blocked `open()`
   * is the silent no-op this whole change exists to remove.
   */
  if (needsServerDownload()) {
    const { url } = await createInvoiceDownloadTicket(invoice.id);
    window.location.assign(`${apiBaseUrl}${url}`);
    return;
  }

  const token = getToken();
  const res = await fetch(`${apiBaseUrl}${invoice.pdfUrl}`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : checkoutAuthHeader(invoice.pdfUrl),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      errors?: Array<{ code?: string }>;
    } | null;
    throw new Error(body?.errors?.[0]?.code ?? 'invoice_download_failed');
  }
  // ASCII filename, matching what the server sets on Content-Disposition.
  saveBlob(await res.blob(), invoiceFileName(invoice.number));
}

/**
 * `УТ-PRO-2026-000048` → `schet-UT-PRO-2026-000048.pdf` — the same name the API
 * sends in `Content-Disposition` (`invoiceFileName` in
 * `services/api/src/modules/billing/invoice/invoice-pdf.ts`; keep the two equal).
 *
 * ASCII only, but the Cyrillic «УТ» is TRANSLITERATED rather than stripped: the
 * old pass dropped it and collapsed dashes before adding the prefix, so a buyer
 * saved `schet--PRO-2026-000048.pdf` (QA 2026-10-22).
 */
export function invoiceFileName(number: string): string {
  const latin = number
    .replace(/УТ/g, 'UT')
    .replace(/[^A-Za-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return `schet-${latin || 'invoice'}.pdf`;
}
